/**
 * Layout Context — Trading Dashboard
 *
 * Manages the tabbed layout workspace state: tabs, widget placements per
 * tab, and active tab selection. State is persisted to localStorage per
 * user and restored on mount.
 *
 * Tab-level concerns live here. The widget-level reducer (add / remove /
 * move / resize) is the shared gridDashboardReducer in components/grid-dashboard.
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
  gridDashboardReducer,
  type WidgetPlacement,
  type WidgetRegistry,
} from '../components/grid-dashboard';
import { tradingWidgetRegistry } from '../config/widgets/registry';
import { LAYOUT_TEMPLATES } from '../config/widgets/templates';

export type { WidgetPlacement } from '../components/grid-dashboard';

export interface LayoutTab {
  id: string;
  name: string;
  widgets: WidgetPlacement[];
}

export interface LayoutState {
  tabs: LayoutTab[];
  activeTabId: string;
}

export type LayoutAction =
  | { type: 'CREATE_TAB'; payload: { name: string; widgets?: WidgetPlacement[] } }
  | { type: 'RENAME_TAB'; payload: { tabId: string; name: string } }
  | { type: 'CLOSE_TAB'; payload: { tabId: string } }
  | { type: 'SWITCH_TAB'; payload: { tabId: string } }
  | { type: 'ADD_WIDGET'; payload: { tabId: string; widgetType: string; config?: Record<string, unknown> } }
  | { type: 'REMOVE_WIDGET'; payload: { tabId: string; instanceId: string } }
  | { type: 'UPDATE_LAYOUT'; payload: { tabId: string; widgets: WidgetPlacement[] } }
  | { type: '_RESET'; payload: LayoutState };

interface PersistedLayoutState {
  version: number;
  tabs: LayoutTab[];
  activeTabId: string;
}

const PERSISTED_STATE_VERSION = 1;

function storageKey(userUuid: string): string {
  return `layout-state:${userUuid}`;
}

function buildDefaultTabs(): LayoutTab[] {
  const tabs: LayoutTab[] = [];
  for (const template of LAYOUT_TEMPLATES.slice(0, 2)) {
    const widgets: WidgetPlacement[] = template.widgets.map((tw) => ({
      instanceId: crypto.randomUUID(),
      widgetType: tw.widgetType,
      x: tw.x,
      y: tw.y,
      w: tw.w,
      h: tw.h,
    }));
    tabs.push({ id: crypto.randomUUID(), name: template.name, widgets });
  }
  return tabs;
}

function validateStoredState(
  stored: PersistedLayoutState,
  registry: WidgetRegistry,
): LayoutState | null {
  const tabs = stored.tabs.map((tab) => ({
    ...tab,
    widgets: tab.widgets.filter((wp) => {
      if (!registry.get(wp.widgetType)) {
        console.warn(
          `[LayoutContext] Skipping widget "${wp.widgetType}" (instanceId: ${wp.instanceId}) — not in registry`,
        );
        return false;
      }
      return true;
    }),
  }));

  if (tabs.length === 0) return null;

  const activeTab = tabs.find((t) => t.id === stored.activeTabId);
  const activeTabId = activeTab ? stored.activeTabId : tabs[0]?.id ?? '';
  return { tabs, activeTabId };
}

/**
 * Apply a widget-level action to the named tab by delegating to the shared
 * gridDashboardReducer. Returns a new LayoutState with the updated tab.
 */
function applyWidgetActionToTab(
  state: LayoutState,
  tabId: string,
  widgetAction: Parameters<typeof gridDashboardReducer>[1],
  registry: WidgetRegistry,
): LayoutState {
  return {
    ...state,
    tabs: state.tabs.map((tab) => {
      if (tab.id !== tabId) return tab;
      const next = gridDashboardReducer({ widgets: tab.widgets }, widgetAction, registry);
      return { ...tab, widgets: next.widgets };
    }),
  };
}

export function layoutReducer(state: LayoutState, action: LayoutAction): LayoutState {
  switch (action.type) {
    case 'CREATE_TAB': {
      const newTabId = crypto.randomUUID();
      const widgets: WidgetPlacement[] = (action.payload.widgets ?? []).map((wp) => ({
        ...wp,
        instanceId: wp.instanceId || crypto.randomUUID(),
      }));
      const newTab: LayoutTab = { id: newTabId, name: action.payload.name, widgets };
      return { ...state, tabs: [...state.tabs, newTab], activeTabId: newTabId };
    }

    case 'RENAME_TAB': {
      return {
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.id === action.payload.tabId ? { ...tab, name: action.payload.name } : tab,
        ),
      };
    }

    case 'CLOSE_TAB': {
      if (state.tabs.length <= 1) return state;
      const remaining = state.tabs.filter((t) => t.id !== action.payload.tabId);
      let { activeTabId } = state;
      if (activeTabId === action.payload.tabId) {
        activeTabId = remaining[0].id;
      }
      return { ...state, tabs: remaining, activeTabId };
    }

    case 'SWITCH_TAB': {
      const exists = state.tabs.some((t) => t.id === action.payload.tabId);
      if (!exists) return state;
      return { ...state, activeTabId: action.payload.tabId };
    }

    case 'ADD_WIDGET': {
      return applyWidgetActionToTab(
        state,
        action.payload.tabId,
        { type: 'ADD_WIDGET', payload: { widgetType: action.payload.widgetType, config: action.payload.config } },
        tradingWidgetRegistry,
      );
    }

    case 'REMOVE_WIDGET': {
      return applyWidgetActionToTab(
        state,
        action.payload.tabId,
        { type: 'REMOVE_WIDGET', payload: { instanceId: action.payload.instanceId } },
        tradingWidgetRegistry,
      );
    }

    case 'UPDATE_LAYOUT': {
      return applyWidgetActionToTab(
        state,
        action.payload.tabId,
        { type: 'UPDATE_LAYOUT', payload: { widgets: action.payload.widgets } },
        tradingWidgetRegistry,
      );
    }

    case '_RESET': {
      return action.payload;
    }

    default:
      return state;
  }
}

interface LayoutContextValue {
  state: LayoutState;
  dispatch: React.Dispatch<LayoutAction>;
}

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

interface LayoutProviderProps {
  children: ReactNode;
}

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const { userUuid } = useAuth();

  const initState = useCallback((): LayoutState => {
    if (userUuid) {
      try {
        const raw = localStorage.getItem(storageKey(userUuid));
        if (raw) {
          const parsed: PersistedLayoutState = JSON.parse(raw);
          if (parsed.version && parsed.tabs && parsed.activeTabId) {
            const validated = validateStoredState(parsed, tradingWidgetRegistry);
            if (validated) return validated;
          }
        }
      } catch (err) {
        console.warn('[LayoutContext] Failed to restore layout from localStorage:', err);
      }
    }

    const defaultTabs = buildDefaultTabs();
    return {
      tabs: defaultTabs,
      activeTabId: defaultTabs[0]?.id ?? '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userUuid]);

  const [state, dispatch] = useReducer(layoutReducer, undefined, initState);

  const prevUserUuid = React.useRef(userUuid);
  useEffect(() => {
    if (prevUserUuid.current === userUuid) return;
    prevUserUuid.current = userUuid;
    dispatch({ type: '_RESET', payload: initState() });
  }, [userUuid, initState]);

  useEffect(() => {
    if (!userUuid) return;
    const persisted: PersistedLayoutState = {
      version: PERSISTED_STATE_VERSION,
      tabs: state.tabs,
      activeTabId: state.activeTabId,
    };
    try {
      localStorage.setItem(storageKey(userUuid), JSON.stringify(persisted));
    } catch (err) {
      console.warn('[LayoutContext] Failed to persist layout to localStorage:', err);
    }
  }, [state, userUuid]);

  const value = React.useMemo(() => ({ state, dispatch }), [state]);

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutContextValue => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

export const useLayoutOptional = (): LayoutContextValue | null => {
  return useContext(LayoutContext) ?? null;
};
